import React from 'react';
import { ShoppingBag, FileText, ClipboardList, BarChart3, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function PurchasingDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.achats.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/achats/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم المشتريات' })}
      subtitle={p('subtitle', { defaultValue: 'متابعة الطلبات والأوامر والموردين' })}
      icon={ShoppingBag}
      accent="from-orange-600 to-amber-800"
      recentTitle={p('recent_orders', { defaultValue: 'آخر الأوامر' })}
      quickLinks={[
        { label: p('link_requests', { defaultValue: 'طلبات الشراء' }), href: '/achats/demandes', icon: FileText },
        { label: p('link_orders', { defaultValue: 'أوامر الشراء' }), href: '/achats/commandes', icon: ClipboardList },
        { label: p('link_receipts', { defaultValue: 'الاستلام' }), href: '/achats/receptions', icon: ShoppingBag },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/achats/analyses-rapports', icon: BarChart3 },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'open',
          'confirmed',
          'partial',
          'approved',
          'active',
        ]);

        return {
          hero: {
            label: p('kpi_month_spend', { defaultValue: 'إنفاق الشهر' }),
            value: formatBrandMoney(kpis.monthSpend || 0),
            icon: ShoppingBag,
          },
          kpis: withKpiVariants([
            { label: p('kpi_pending_requests', { defaultValue: 'طلبات معلقة' }), value: kpis.pendingRequests ?? 0, icon: FileText },
            { label: p('kpi_active_orders', { defaultValue: 'أوامر نشطة' }), value: kpis.activeOrders ?? 0, icon: ClipboardList },
            { label: p('kpi_suppliers', { defaultValue: 'الموردون' }), value: kpis.suppliers ?? 0, icon: ShoppingBag },
            {
              label: p('kpi_total_orders', { defaultValue: 'إجمالي الطلبات' }),
              value: (kpis.pendingRequests ?? 0) + (kpis.activeOrders ?? 0),
              icon: Layers,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline', { defaultValue: 'حالة أوامر الشراء' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'طلبات وأوامر وموردون' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_pending_requests', { defaultValue: 'طلبات معلقة' }), value: kpis.pendingRequests ?? 0 },
                { label: p('kpi_active_orders', { defaultValue: 'أوامر نشطة' }), value: kpis.activeOrders ?? 0 },
                { label: p('kpi_suppliers', { defaultValue: 'الموردون' }), value: kpis.suppliers ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الأوامر' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'نشطة مقابل متوقفة' }),
              active: active || kpis.activeOrders || 0,
              inactive: inactive || kpis.pendingRequests || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات الشراء' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب حالة الأمر' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
